import { useState } from 'react';
import { FiRotateCcw, FiX } from 'react-icons/fi';
import { MdContactSupport, MdTerminal } from 'react-icons/md';
import { toast } from 'react-toastify';

import { Button } from '@/components/button/button';
import { DeploymentStatusBadge } from '@/components/deploymentStatus/deploymentStatus';
import { Icon } from '@/components/icon/icon';
import Modal from '@/components/modal/modal';
import { TroubleshootingTips } from '@/components/troubleshootingTips/troubleshootingTips';
import { Heading } from '@/components/typography/heading';
import { useAgentRestart } from '@/hooks/mutations/useAgentMutations';
import { DeploymentStatus } from '@/types/agent';
import envVarsProvider from '@/utils/envVars/envVars';

import styles from './agentDeploymentStatusPanel.module.scss';

type Props = {
  deploymentStatus: DeploymentStatus.ERRORED | DeploymentStatus.DEGRADED;
  errorMessage?: string;
  agentId: string;
};

type ActiveModal = 'restart' | 'logsCliCommands' | 'redeployCliCommands' | 'contact' | null;

export function AgentDeploymentStatusPanel( { deploymentStatus, errorMessage, agentId }: Props ) {
  const [ activeModal, setActiveModal ] = useState<ActiveModal>( null );
  const restartMutation = useAgentRestart();

  const isErrored = deploymentStatus === DeploymentStatus.ERRORED;

  const handleRestartConfirm = () => {
    restartMutation.mutate( { agentId }, {
      onSuccess: () => toast.success( 'Agent restarted' ),
      onError: () => toast.error( 'Unable to restart agent' )
    } );
    setActiveModal( null );
  };

  const statusSubtitle = isErrored
    ? 'Agent cannot process requests due to an error'
    : 'Agent online with reduced capacity';

  const detailsLabel = isErrored ? 'Error Details' : 'Details';

  const detailsText = isErrored
    ? errorMessage
    : 'One or more servers are unavailable. The agent is still processing requests.';

  const detailsSubtitle = isErrored
    ? 'This error is often recoverable. Start with the steps below.'
    : 'This state is often recoverable. Try the steps below if needed.';

  const step1Title = isErrored
    ? 'Query CLI Logs for more details'
    : 'Query CLI agent logs for more details';

  const step1Description = isErrored
    ? 'Detailed information in the agent logs can help identify configuration issues, missing dependencies, or runtime failures.'
    : 'Instance-level errors may reveal resource limits, startup failures, or transient platform issues.';

  const tips = [
    {
      title: step1Title,
      description: step1Description,
      actions: [ { label: 'View CLI commands', icon: MdTerminal, onClick: () => setActiveModal( 'logsCliCommands' ) } ]
    },
    {
      title: 'Restart the agent process',
      description: 'Restarting the agent process may recover from temporary failures without requiring a redeploy.',
      actions: [ { label: 'Restart agent', icon: FiRotateCcw, onClick: () => setActiveModal( 'restart' ) } ]
    },
    {
      title: 'Redeploy the agent (Advanced Step)',
      description: 'If earlier steps do not resolve the issue, in the CLI delete and redeploy the agent.',
      actions: [ { label: 'View CLI commands', icon: MdTerminal, onClick: () => setActiveModal( 'redeployCliCommands' ) } ]
    },
    {
      title: 'Contact Support',
      description: 'If the issue persists, contact support with the error details and logs for further assistance.',
      actions: [ { label: 'Contact us', icon: MdContactSupport, onClick: () => setActiveModal( 'contact' ) } ]
    }
  ];

  return (
    <div className={ styles.panel } data-testid="deployment-status-panel">
      <div className={ styles.header }>
        <div className={ styles.statusColumn }>
          <span className={ styles.columnLabel }>Deployment Status</span>
          <DeploymentStatusBadge status={ deploymentStatus } variant="large" />
          <p className={ styles.statusSubtitle }>{ statusSubtitle }</p>
        </div>
        <div className={ styles.verticalDivider } />
        <div className={ styles.detailsColumn }>
          <span className={ styles.columnLabel }>{ detailsLabel }</span>
          { detailsText && <p className={ styles.detailsText }>{ detailsText }</p> }
          <p className={ styles.detailsSubtitle }>{ detailsSubtitle }</p>
        </div>
      </div>

      <div className={ styles.horizontalDivider } />

      <TroubleshootingTips tips={ tips } />

      { activeModal === 'logsCliCommands' && (
        <Modal>
          <div className={ styles.modalHeader }>
            <Heading as="h2" size="md">Query CLI agent logs for more details</Heading>
            <button className={ styles.modalClose } onClick={ () => setActiveModal( null ) } aria-label="Close">
              <Icon icon={ FiX } />
            </button>
          </div>
          <p className={ styles.modalBody }>To query the agent logs in the command line interface, type:</p>
          <code className={ styles.cliCommand }>uv run aidp_cli agent logs -a { agentId }</code>
        </Modal>
      ) }

      { activeModal === 'restart' && (
        <Modal>
          <Heading as="h2" size="md">Restart agent?</Heading>
          <p className={ styles.modalBody }>This will restart the agent&apos;s running instances.</p>
          <p className={ styles.modalBodySecondary }>In-flight requests may be briefly interrupted.</p>
          <div className={ styles.modalActions }>
            <Button variant='secondary' size='sm' onClick={ () => setActiveModal( null ) }>Cancel</Button>
            <Button variant='primary' size='sm' onClick={ handleRestartConfirm }>Restart Agent</Button>
          </div>
        </Modal>
      ) }

      { activeModal === 'redeployCliCommands' && (
        <Modal>
          <div className={ styles.modalHeader }>
            <Heading as="h2" size="md">Redeploy the agent (Advanced Step)</Heading>
            <button className={ styles.modalClose } onClick={ () => setActiveModal( null ) } aria-label="Close">
              <Icon icon={ FiX } />
            </button>
          </div>
          <p className={ styles.modalBody }>To query the agent logs in the command line interface, type:</p>
          <code className={ styles.cliCommand }>uv run aidp_cli agent delete -a { agentId }</code>
          <code className={ styles.cliCommand }>uv run aidp_cli agent publish --build-path /path/to/my/agent</code>
        </Modal>
      ) }

      { activeModal === 'contact' && (
        <Modal>
          <div className={ styles.modalHeader }>
            <Heading as="h2" size="md">Contact us</Heading>
            <button className={ styles.modalClose } onClick={ () => setActiveModal( null ) } aria-label="Close">
              <Icon icon={ FiX } />
            </button>
          </div>
          <p className={ styles.modalBody }>Email: <a href={ `mailto:${ envVarsProvider.get( 'CONTACT_US_EMAIL' ) }` }>{ envVarsProvider.get( 'CONTACT_US_EMAIL' ) }</a></p>
        </Modal>
      ) }
    </div>
  );
}
